<script setup lang="ts">
import ProductFeatures from '@/pages/product-detail/ui/productFeatures.vue';
import type { IProduct } from '@/widgets/productCard/model/types/product';

const { product } = defineProps<{ product: IProduct }>();

const { t } = useI18n();

const sortedFeatures = computed(() => {
  if (!product.features) return [];
  return [...product.features].sort((a, b) => a.order - b.order);
});

const availableTabs = computed(() => {
  const list: { id: number; label: string; value: 'composition' | 'features' }[] = [];
  if (product.composition && product.composition.trim().length > 0) {
    list.push({ id: 1, label: t('composition'), value: 'composition' });
  }
  if (sortedFeatures.value.length > 0) {
    list.push({ id: 2, label: t('features'), value: 'features' });
  }
  return list;
});

const activeTab = ref<'composition' | 'features' | ''>('');

watchEffect(() => {
  const tabs = availableTabs.value;
  if (!tabs.length) {
    activeTab.value = '';
    return;
  }
  if (!tabs.find(tab => tab.value === activeTab.value)) {
    activeTab.value = tabs[0].value;
  }
});
</script>

<template>
  <div v-if="availableTabs.length" class="products-tabs">
    <div class="products-tabs__nav">
      <button
        v-for="tab in availableTabs"
        :key="tab.id"
        type="button"
        :class="['products-tabs__tab', { _active: tab.value === activeTab }]"
        @click="activeTab = tab.value"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="products-tabs__content">
      <Transition name="products-tabs-fade" mode="out-in">
        <div v-if="activeTab === 'composition'" key="composition">
          <p class="products-tabs__text" :class="{ _empty: !product.composition }">{{ product.composition }}</p>
        </div>
        <div v-else-if="activeTab === 'features'" key="features">
          <ProductFeatures :characteristics="sortedFeatures" />
        </div>
      </Transition>
    </div>
  </div>
  
</template>

<style lang="less">
.products-tabs {
  &__nav {
    display: flex;
    gap: @size-x6;
    border-bottom: 1px solid @color-silver-chalice;
  }

  &__tab {
    position: relative;
    background: transparent;
    border: 0;
    padding: @size-x3 0;
    margin: 0;
    cursor: pointer;
    color: #222;
    font-size: 18px;
    font-weight: 600;
    line-height: 130%;
    font-family: Onest;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px; // sits over the nav's border
      height: 2px;
      background: transparent;
      transition: background @A;
    }

    &._active::after {
      background: @color-tango;
    }
  }

  &__content {
    padding-top: @size-x6;
  }

  &__text {
    font-size: @size-x4;
    font-weight: 400;
    line-height: 130%;
    word-break: break-word;
    margin: 0;

    &._empty {
      color: @color-silver-chalice;
      font-style: italic;
    }
  }

  @media @1024 {
    &__nav {
      gap: @size-x4;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }

    &__tab {
      padding: @size-x2 0;
      font-size: @size-x4;
    }

    &__content {
      padding-top: @size-x4;
    }

    &__text {
      font-size: 14px;
    }
  }
}

.products-tabs-fade-enter-active,
.products-tabs-fade-leave-active {
  transition: opacity @A;
}

.products-tabs-fade-enter-from,
.products-tabs-fade-leave-to {
  opacity: 0;
}
</style>

